import React, { createContext, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ApiResults } from '../../../models/ApiResults';
import { PaymentSheetListObj } from '../../../models/PaymentSheet';
import { PaymentSheetService } from '../../../services/PaymentSheet.service';
import { hideSpinner, showSpinner } from '../../../store/slicers/globalSpinner.slicer';
import { showSnackbarAlert } from '../../../store/slicers/snackbarAlert.slicer';
import { checkResponseStatus } from '../../../utils/api/response';

export interface PaymentSheetsFilterFields extends PaymentSheetListObj {
  order_by?: string;
  page?: number;
  page_size?: number;
  search?: string;
}

export interface PaymentSheetsResult extends ApiResults {
  results: PaymentSheetListObj[];
}

interface PaymentSheetsContextProps {
  payments: PaymentSheetsResult | undefined;
  fetchPaymentSheets: () => void;
  filterFields: PaymentSheetsFilterFields;
  setFilterFields: React.Dispatch<React.SetStateAction<PaymentSheetsFilterFields>>;
  page: number;
  rowsPerPage: number;
  handleOrderChange: (property: string, order: 'asc' | 'desc') => void;
  handlePageChange: (page: number) => void;
  handleOnChangeRowsPerPage: (page_size: number) => void;
}

const PaymentSheetsPageContext = createContext<PaymentSheetsContextProps | undefined>(undefined);

interface Props {
  children: React.ReactNode;
}

export const PaymentSheetsPageContextProvider = ({ children }: Props) => {
  const dispatch = useDispatch();
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);

  const [payments, setPayments] = useState<PaymentSheetsResult | undefined>(undefined);
  const [filterFields, setFilterFields] = useState<PaymentSheetsFilterFields>({} as PaymentSheetsFilterFields);

  const fetchPaymentSheets = async () => {
    dispatch(showSpinner());

    try {
      const response = await PaymentSheetService.getPayments();
      if (checkResponseStatus(response)) {
        setPayments({
          results: response?.data,
          count: response?.data?.length,
          page_count: 1,
          page_number: 1
        });
      }
    } catch (error: any) {
      dispatch(
        showSnackbarAlert({ title: 'Erro', message: error?.data || 'Houve um erro ao processar a sua solicitação', severity: 'error' })
      );
    } finally {
      dispatch(hideSpinner());
    }
  };

  const handleOrderChange = (property: string, order: 'asc' | 'desc') => {
    const orderFilter = order === 'asc' ? property : `-${property}`;
    setFilterFields({ ...filterFields, order_by: orderFilter });
  };

  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const handleOnChangeRowsPerPage = (page_size: number) => {
    setRowsPerPage(page_size);
  };

  useEffect(() => {
    fetchPaymentSheets();
  }, [filterFields, page, rowsPerPage]);

  const contextValue: PaymentSheetsContextProps = {
    payments,
    fetchPaymentSheets,
    filterFields,
    setFilterFields,
    page,
    rowsPerPage,
    handleOrderChange,
    handlePageChange,
    handleOnChangeRowsPerPage
  };

  return <PaymentSheetsPageContext.Provider value={contextValue}>{children}</PaymentSheetsPageContext.Provider>;
};

export const usePaymentSheetsPageContext = () => {
  const context = useContext(PaymentSheetsPageContext);
  if (!context) {
    throw new Error('usePaymentSheetsPageContext must be used within a PaymentSheetsPageContextProvider');
  }
  return context;
};
